/**
 * "Enable animations anyway" — the escape hatch out of the reduced-motion poster.
 *
 * It exists because `prefers-reduced-motion` is a blunt instrument that a lot of people have set
 * for a reason that is not this page. It is switched on by default on some Android battery-saver
 * profiles, it is what you set to stop a parallax marketing site from making you ill, and it is
 * what a screen recorder turns on. This site is a cartoon of zombies hammering on a wall: some of
 * the visitors who have that setting want to see it, and telling them to go and change an OS
 * preference to look at a joke is not an answer.
 *
 * So it is shown *only* to visitors whose system asked for reduced motion — on any other page it
 * would be a control that does nothing — and it only ever turns motion on. There is deliberately
 * no stored way to turn it off again from here beyond pressing it a second time: the OS setting is
 * the place that decision belongs, and a second switch that could contradict it is a setting the
 * visitor has to find twice.
 */
import { useCallback, useEffect, useState } from 'react'
import { motionPreference, motionToggleApplies, setMotionPreference } from '@/render/poster'

export function MotionToggle() {
  // Read during the first render rather than in an effect, exactly as `SoundToggle` does: this is
  // external state that exists before the component, and applying it afterwards paints the wrong
  // label for a frame.
  const [applies, setApplies] = useState(motionToggleApplies)
  const [animating, setAnimating] = useState(() => motionPreference() === 'animate')

  useEffect(() => {
    // The OS preference can be turned on while the tab is open — a battery saver kicking in, or
    // somebody changing it precisely because this page is moving too much.
    if (typeof window.matchMedia !== 'function') return
    const media = window.matchMedia('(prefers-reduced-motion: reduce)')
    if (typeof media.addEventListener !== 'function') return
    const onChange = (): void => setApplies(motionToggleApplies())
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [])

  const toggle = useCallback(() => {
    const next = motionPreference() === 'animate' ? 'system' : 'animate'
    // The scene subscribes to this through `onMotionChange` and restarts (or stops) its own
    // ticker; nothing here reaches into the renderer, which keeps `src/ui` free of Pixi.
    setMotionPreference(next)
    setAnimating(next === 'animate')
  }, [])

  if (!applies) return null

  return (
    <button
      type="button"
      onClick={toggle}
      aria-pressed={animating}
      className="rounded-full px-3 py-1.5 text-sm font-semibold text-ink/70 transition-colors hover:bg-ink/10 hover:text-ink"
    >
      {animating ? 'Animations on' : 'Enable animations anyway'}
    </button>
  )
}
